import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@mui/material";
import { Plus } from "lucide-react";
import { ChairInput, PlaybackMode } from "../types";
import { RoleDef, defaultVoiceForRole } from "../lib/roleSets";
import { createChairEngine } from "../lib/chairAudioEngine";
import { postChair } from "../api";
import { useToast } from "../../../components/Toast";
import { ChairRow } from "./ChairRow";
import { PlaybackControls } from "./PlaybackControls";
import { ChairSuggest } from "./ChairSuggest";

interface EnsembleVoiceToolProps {
  patientId: string;
  sessionId?: string;
  language: "en" | "hu";
  getToken: () => Promise<string | null>;
  roles: RoleDef[];
  roleI18nPrefix: string;
  roleLabelKey: string;
  playback: PlaybackMode; // "together" = chorus (schema chair, bus); "sequence" = dialogue (empty chair, parts)
  addLabelKey: string;    // e.g. "experiential.addChair" or "experiential.addTurn"
  spatial?: boolean;      // HRTF-position each voice around the listener (Spatial Modes)
  suggestFromSessions?: boolean; // schema modes: LLM prefill from selected sessions
}

export function EnsembleVoiceTool({
  patientId, sessionId, language, getToken,
  roles, roleI18nPrefix, roleLabelKey, playback, addLabelKey, spatial = false, suggestFromSessions = false,
}: EnsembleVoiceToolProps) {
  const { t } = useTranslation();
  const toast = useToast();

  const [chairs, setChairs] = useState<ChairInput[]>([]);
  // Why the suggestion engine proposed each chair (display only, never sent).
  const [rationale, setRationale] = useState<Record<string, string>>({});
  const [gains, setGains] = useState<Record<string, number>>({});
  const [generated, setGenerated] = useState(false);
  const [busy, setBusy] = useState(false);
  const [looping, setLooping] = useState(false);

  const engineRef = useRef<ReturnType<typeof createChairEngine> | null>(null);

  const addChair = () => {
    setChairs((prev) => {
      // Two-role dialogues (empty chair) alternate speakers by default.
      const role = roles.length === 2 ? roles[prev.length % 2] : roles[0];
      return [...prev, {
        id: crypto.randomUUID(),
        mode: role.key,
        label: "",
        text: "",
        voice: role.defaultVoice,
      }];
    });
  };

  const updateChair = (idx: number, next: ChairInput) => {
    setChairs((prev) => {
      const updated = [...prev];
      const old = updated[idx];
      // If role changed, update default voice
      if (old.mode !== next.mode) {
        next = { ...next, voice: defaultVoiceForRole(roles, next.mode) };
      }
      updated[idx] = next;
      return updated;
    });
  };

  const removeChair = (idx: number) => {
    setChairs((prev) => prev.filter((_, i) => i !== idx));
  };

  const onGenerate = async () => {
    try {
      setBusy(true);
      await engineRef.current?.close();
      const res = await postChair(getToken, { patient_id: patientId, session_id: sessionId, language, chairs });
      const engine = createChairEngine(res.clips, { spatial });
      await engine.load((url) => fetch(url).then((r) => r.arrayBuffer()));
      engineRef.current = engine;
      setGenerated(true);
    } catch (e) {
      toast.error(`${t("experiential.genError")}: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const handleGain = (chairId: string, v: number) => {
    engineRef.current?.setGain(chairId, v);
    setGains((prev) => ({ ...prev, [chairId]: v }));
  };

  useEffect(() => {
    return () => {
      engineRef.current?.close();
    };
  }, []);

  return (
    <div>
      {suggestFromSessions && (
        <ChairSuggest
          patientId={patientId}
          language={language}
          getToken={getToken}
          hasContent={chairs.some((c) => c.text.trim())}
          onSuggest={(suggested) => {
            const next = suggested.map((s) => ({ id: crypto.randomUUID(), mode: s.mode, label: s.label, text: s.text, voice: s.voice }));
            engineRef.current?.close();
            engineRef.current = null;
            setGenerated(false);
            setGains({});
            setChairs(next);
            setRationale(Object.fromEntries(next.map((c, i) => [c.id, suggested[i].rationale])));
          }}
        />
      )}
      {spatial && (
        <p style={{ margin: "0 0 8px", fontSize: 13, fontStyle: "italic", opacity: 0.7 }}>
          {t("experiential.spatialHint")}
        </p>
      )}
      {chairs.map((chair, idx) => (
        <React.Fragment key={chair.id}>
        {rationale[chair.id] && (
          <p style={{ margin: "8px 0 2px", fontSize: 12, fontStyle: "italic", color: "var(--color-muted)" }}>
            {rationale[chair.id]}
          </p>
        )}
        <ChairRow
          chair={chair}
          onChange={(next) => updateChair(idx, next)}
          onRemove={() => removeChair(idx)}
          gain={gains[chair.id] ?? 1}
          onGain={(v) => handleGain(chair.id, v)}
          generated={generated}
          roles={roles}
          roleI18nPrefix={roleI18nPrefix}
          roleLabelKey={roleLabelKey}
        />
        </React.Fragment>
      ))}

      <Button
        startIcon={<Plus size={14} />}
        onClick={addChair}
        size="small"
        variant="outlined"
        sx={{ mt: 1, mb: 1 }}
      >
        {t(addLabelKey)}
      </Button>

      <div className="exp-chair-controls">
        <Button
          variant="contained"
          size="small"
          onClick={onGenerate}
          disabled={busy || chairs.length === 0}
        >
          {t("experiential.generate")}
        </Button>

        {generated && (
          <PlaybackControls
            onPlay={() => engineRef.current?.play(playback)}
            onStop={() => engineRef.current?.stop()}
            looping={looping}
            onToggleLoop={() => {
              const next = !looping;
              setLooping(next);
              engineRef.current?.setLoop(next);
            }}
          />
        )}
      </div>
    </div>
  );
}
